import { useCallback, useRef, useState } from 'react';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import WatermarkPctgInput from './controls/watermark-pctg-input';
import WatermarkAxisInput from './controls/watermark-axis-input';
import WatermarkRotateInput from './controls/watermark-rotate-input';
import Checkbox from '@ui/checkbox/checkbox';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import type { IWatermarkElement } from '@/types/watermarks.types';
import UploadLarge from '@ui/uploads/upload-large';
import Icon from '@ui/icons/icon';
import { generateBorderDashSvg } from '@/micros/edit-project/helpers/constants/border-constants';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import { GetUploadEndpoint, getUploadFilename, UploadFileToProvider } from '@ui/uploads/validations';
import { isObject } from 'underscore';
import WatermarkDurationSelector from './controls/watermark-duration-selector';

type Settings = IWatermarkElement['settings'];

interface AddWatermarkImageBlockProps {
  settings: Settings;
  onSettingsChange: (settings: Settings) => void;
}

const AddWatermarkImageBlock = ({ settings, onSettingsChange }: AddWatermarkImageBlockProps) => {
  const handleUpdate = useCallback(
    <K extends keyof Settings>(key: K, value: Settings[K]) => {
      onSettingsChange({ ...settings, [key]: value });
    },
    [settings, onSettingsChange]
  );

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | FileList) => {
    try {
      if (file instanceof File) {
        setUploading(true);

        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          if (finalUrl) {
            onSettingsChange({
              ...settings,
              image: {
                ...settings.image,
                url: finalUrl,
                original_url: settings.image.original_url || finalUrl,
              },
            });
          }
        }
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUploading(false);
    }
  };

  const hasImage = Boolean(settings.image.url);

  return (
    <Container className="flex-column gap-16">
      <Container className="flex-column gap-8">
        {!settings.image.url ? (
          <Container
            background="var(--gray-900)"
            height="64px"
            borderRadius="16px"
            style={{
              backgroundImage: hasImage ? undefined : `url(${generateBorderDashSvg('#9B97A1')})`,
            }}
          >
            <UploadLarge
              placeholder=""
              wrapperClassNames="height-100-percent"
              CustomTitle={
                <>
                  <Text className="L2 flex ai-center gap-8" design="GlobalIcoText1">
                    <Icon name={ICONS.ADD} size={12} />
                    <span>Upload Image</span>
                  </Text>
                  <Text className="P3" design="GlobalIcoText3">
                    or drag and drop them here
                  </Text>
                </>
              }
              onFile={handleFile}
              uploading={uploading}
              allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
            />
          </Container>
        ) : (
          <Container
            className="flex ai-center jc-space-between gap-8 padding-h-12 radius-12 height-48"
            design="GlobalCont2"
          >
            <img
              src={settings.image.url}
              alt=""
              style={{ height: '40px', width: '40px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
            />
            <Text className="P2 flex-1 text-truncate" color="var(--global-ico-text-1)">
              {settings.image.url.split('/').pop()?.split('?')[0] ?? 'Image'}
            </Text>
            <Button
              clear
              width="24px"
              height="24px"
              className="flex ai-center jc-center"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name={ICONS.EDIT} size={12} color="var(--global-ico-text-3)" />
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpg,image/jpeg,image/png,image/gif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.currentTarget.value = '';
              }}
            />
          </Container>
        )}

        <Container className="flex gap-8 ai-flex-end">
          <WatermarkPctgInput
            label="Opacity"
            min={0}
            value={String(Math.round((settings.image.opacity ?? 1) * 100))}
            disabled={!hasImage}
            onChange={(val) => {
              const n = parseFloat(val);
              if (!isNaN(n))
                onSettingsChange({ ...settings, image: { ...settings.image, opacity: Math.min(100, n) / 100 } });
            }}
          />
          <WatermarkPctgInput
            label="Scale"
            value={String(Math.round(settings.dimensions.scale * 100))}
            disabled={!hasImage}
            onChange={(val) => {
              const n = parseFloat(val);
              if (!isNaN(n)) handleUpdate('dimensions', { ...settings.dimensions, scale: n / 100 });
            }}
          />
        </Container>

        <Container className="flex gap-8">
          <WatermarkAxisInput
            prefix="X"
            value={isNaN(settings.dimensions.x) ? '' : settings.dimensions.x.toString()}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, x: parseFloat(val) })}
            disabled={!hasImage || settings.repeat}
          />
          <WatermarkAxisInput
            prefix="Y"
            value={isNaN(settings.dimensions.y) ? '' : settings.dimensions.y.toString()}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, y: parseFloat(val) })}
            disabled={!hasImage || settings.repeat}
          />
          <WatermarkRotateInput
            value={String(settings.dimensions.rotation ?? 0)}
            onChange={(val) => handleUpdate('dimensions', { ...settings.dimensions, rotation: parseFloat(val) || 0 })}
            disabled={!hasImage}
          />
        </Container>

        <Checkbox
          label={
            <Text className="L2" design="GlobalIcoText3">
              Repeat as pattern
            </Text>
          }
          color="var(--global-ico-text-3)"
          className="S2 flex gap-6"
          checked={settings.repeat}
          value="repeat"
          onChange={(_val, isOn) => handleUpdate('repeat', isOn)}
          selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
          unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
          disabled={!hasImage}
        />
      </Container>

      <WatermarkDurationSelector
        value={settings.duration}
        onChange={(val) => handleUpdate('duration', val)}
        disabled={!hasImage}
      />
    </Container>
  );
};

export default AddWatermarkImageBlock;
